import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { Button } from '@/components/ui/button';
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Heading1, Heading2, Heading3, Link as LinkIcon, Minus,
  Table as TableIcon, Undo, Redo, Pilcrow, Eye, Code2
} from 'lucide-react';
import { useState, useEffect } from 'react';

function ToolBtn({ onClick, active, title, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`h-8 w-8 flex items-center justify-center rounded transition-colors ${
        active ? 'bg-cyan-100 text-cyan-700' : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="mx-1 h-5 w-px bg-gray-200" />;
}

export default function WysiwygEditor({ value, onChange, placeholder }) {
  const [mode, setMode] = useState('edit'); // edit | preview | html

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Image.configure({ inline: false, allowBase64: true, HTMLAttributes: { class: 'procedure-image max-w-full h-auto rounded' } }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: 'text-cyan-600 underline' } }),
      Table.configure({ resizable: true, HTMLAttributes: { class: 'procedure-table' } }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value || '',
    onUpdate: ({ editor: e }) => {
      onChange(e.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none p-4 min-h-[320px] focus:outline-none',
        'data-testid': 'wysiwyg-editor-content',
      },
      transformPastedHTML(html) {
        // Paste from Word cleanup: remove MSO classes/styles, o:p tags
        return html
          .replace(/<!--\[if[^\]]*\]>[\s\S]*?<!\[endif\]-->/g, '')
          .replace(/<o:p>[\s\S]*?<\/o:p>/g, '')
          .replace(/ class="Mso[^"]*"/g, '')
          .replace(/ style="[^"]*mso-[^"]*"/g, '')
          .replace(/ style="[^"]*font-family:[^"]*"/g, '')
          .replace(/ lang="[^"]*"/g, '');
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    if ((value || '') !== editor.getHTML()) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('URL del link:', prev || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 2, withHeaderRow: true }).run();
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white" data-testid="wysiwyg-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-gray-100 bg-gray-50">
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          active={editor.isActive('heading', { level: 1 })} title="Titolo H1" testid="toolbar-h1">
          <Heading1 className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })} title="Titolo H2" testid="toolbar-h2">
          <Heading2 className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive('heading', { level: 3 })} title="Titolo H3" testid="toolbar-h3">
          <Heading3 className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().setParagraph().run()}
          active={editor.isActive('paragraph')} title="Paragrafo" testid="toolbar-p">
          <Pilcrow className="w-4 h-4" />
        </ToolBtn>
        <Divider />
        <ToolBtn onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')} title="Grassetto" testid="toolbar-bold">
          <Bold className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')} title="Corsivo" testid="toolbar-italic">
          <Italic className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')} title="Sottolineato" testid="toolbar-underline">
          <UnderlineIcon className="w-4 h-4" />
        </ToolBtn>
        <Divider />
        <ToolBtn onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')} title="Elenco puntato" testid="toolbar-ul">
          <List className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')} title="Elenco numerato" testid="toolbar-ol">
          <ListOrdered className="w-4 h-4" />
        </ToolBtn>
        <Divider />
        <ToolBtn onClick={setLink} active={editor.isActive('link')} title="Link" testid="toolbar-link">
          <LinkIcon className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn onClick={insertTable} title="Inserisci tabella" testid="toolbar-table">
          <TableIcon className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Separatore" testid="toolbar-hr">
          <Minus className="w-4 h-4" />
        </ToolBtn>
        <Divider />
        <ToolBtn onClick={() => editor.chain().focus().undo().run()} title="Annulla" testid="toolbar-undo">
          <Undo className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().redo().run()} title="Ripristina" testid="toolbar-redo">
          <Redo className="w-4 h-4" />
        </ToolBtn>

        <div className="ml-auto flex items-center gap-0.5">
          <Button type="button" size="sm" variant={mode === 'edit' ? 'default' : 'ghost'}
            onClick={() => setMode('edit')}
            className={`h-7 px-2 text-xs ${mode === 'edit' ? 'bg-cyan-600 hover:bg-cyan-700' : ''}`}
            data-testid="wysiwyg-mode-edit">
            Modifica
          </Button>
          <Button type="button" size="sm" variant={mode === 'preview' ? 'default' : 'ghost'}
            onClick={() => setMode('preview')}
            className={`h-7 px-2 text-xs gap-1 ${mode === 'preview' ? 'bg-cyan-600 hover:bg-cyan-700' : ''}`}
            data-testid="wysiwyg-mode-preview">
            <Eye className="w-3 h-3" /> Anteprima
          </Button>
          <Button type="button" size="sm" variant={mode === 'html' ? 'default' : 'ghost'}
            onClick={() => setMode('html')}
            className={`h-7 px-2 text-xs gap-1 ${mode === 'html' ? 'bg-cyan-600 hover:bg-cyan-700' : ''}`}
            data-testid="wysiwyg-mode-html">
            <Code2 className="w-3 h-3" /> HTML
          </Button>
        </div>
      </div>

      {/* Editor body */}
      {mode === 'edit' && <EditorContent editor={editor} placeholder={placeholder} />}
      {mode === 'preview' && (
        <div className="prose prose-sm max-w-none p-4 min-h-[320px]"
          dangerouslySetInnerHTML={{ __html: editor.getHTML() }}
          data-testid="wysiwyg-preview" />
      )}
      {mode === 'html' && (
        <textarea
          value={editor.getHTML()}
          onChange={(e) => { editor.commands.setContent(e.target.value); onChange(e.target.value); }}
          className="w-full min-h-[320px] p-4 text-xs font-mono text-gray-700 focus:outline-none resize-y"
          data-testid="wysiwyg-html" />
      )}
    </div>
  );
}
